import dayjs from 'dayjs'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import { skipToken } from '@reduxjs/toolkit/query'

import { ConsultationStatus, ConsultationType, CreateConsultationDto } from '@/core/models/consultation.model'
import { WaitingRoom } from '@/core/models/waiting-room.model'
import { useCreateConsultationMutation, useUpdateConsultationMutation } from '@/core/services/consultation'
import { useConfirmPaymentMutation, useStartPaymentByReceiverIDMutation } from '@/core/services/charge'
import { useGetStaffQuery } from '@/core/services/staff'
import { getApiErrorMessage } from '@/utils/errors'

import PayConsultationDialog from '../components/PayConsultationDialog'

export type ReserveConsultationValues = { patient: string; speciality?: string; reason?: string }

type Options = {
  /** Called once a priced consultation has been paid (the free flow navigates to the live consultation instead). */
  onPaid?: (consultationId: string) => void
}

/**
 * Queues a patient in a waiting room the way the "reserve" dialog does: free room → live consultation,
 * priced room → card payment dialog (closing it cancels the consultation).
 * Returns the `paymentDialog` element, to render next to the caller's UI.
 */
export function useReserveConsultation(waitingRoom: WaitingRoom | undefined, { onPaid }: Options = {}) {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [openPaymentDialog, setOpenPaymentDialog] = useState<boolean>(false)

  const { data: paymentReceiver } = useGetStaffQuery(waitingRoom?.paymentReceiverId ?? skipToken)
  const [createConsultation, { data: newlyCreatedConsultation, isLoading: isReserving }] = useCreateConsultationMutation()
  const [updateConsultation] = useUpdateConsultationMutation()
  const [acceptPaymentFromUserID] = useStartPaymentByReceiverIDMutation()
  const [confirmPayment] = useConfirmPaymentMutation()

  const reserve = async ({ patient: patientId, speciality, reason }: ReserveConsultationValues) => {
    if (!waitingRoom) return

    const createConsultationDto: CreateConsultationDto = {
      patient: patientId,
      speciality,
      startDate: dayjs().toISOString(),
      status: ConsultationStatus.Scheduled,
      type: ConsultationType.Classic,
      waitingRoom: waitingRoom._id,
      price: waitingRoom.price,
    }

    if (reason) createConsultationDto.reason = reason

    try {
      const consultation = await createConsultation(createConsultationDto).unwrap()
      enqueueSnackbar(t('notification.consultation.create.success'), { variant: 'success' })
      // if waiting room has payment enabled, open payment dialog
      if (waitingRoom.price !== 0) {
        setOpenPaymentDialog(true)
      } else {
        navigate(`/consultations/${consultation._id}/live`)
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.create.error')), { variant: 'error' })
    }
  }

  const handleStartPayment = async (stripe: Stripe, elements: StripeElements) => {
    if (!newlyCreatedConsultation || !waitingRoom?.paymentReceiverId) return
    const { _id: consultationId, patient } = newlyCreatedConsultation
    try {
      setIsProcessingPayment(true)
      const paymentIntent = await acceptPaymentFromUserID({ consultationId, receiverID: waitingRoom.paymentReceiverId }).unwrap()
      const { client_secret: clientSecret } = paymentIntent

      if (!clientSecret) throw new Error('client secret is null')

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement('cardNumber')!,
          billing_details: {
            name: `${patient.userInfo.name} ${patient.userInfo.surname}`,
          },
        },
      })

      if (error) throw error

      await confirmPayment(consultationId).unwrap()
      setOpenPaymentDialog(false)
      enqueueSnackbar(t('notification.consultation.acceptPayment.success'), { variant: 'success' })
      onPaid?.(consultationId)
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.acceptPayment.error')), { variant: 'error' })
    } finally {
      setIsProcessingPayment(false)
    }
  }

  const handleQuitOnPaymentDialog = () => {
    setOpenPaymentDialog(false)
    if (!newlyCreatedConsultation) return
    // delete created consultation ???
    updateConsultation({ id: newlyCreatedConsultation._id, changes: { status: ConsultationStatus.Canceled } }).then(() => {
      enqueueSnackbar(t('notification.consultation.canceled.success'), { variant: 'info' })
    })
  }

  const paymentDialog = newlyCreatedConsultation && waitingRoom?.paymentReceiverId && (
    <PayConsultationDialog
      isPaymentReceiverStripeActivated={!!paymentReceiver?.stripeActivated}
      open={openPaymentDialog}
      onClose={handleQuitOnPaymentDialog}
      onPaymentStart={handleStartPayment}
      paymentLoading={isProcessingPayment}
      price={newlyCreatedConsultation?.price || 0}
    />
  )

  return { reserve, isReserving, paymentDialog }
}
